import { useState } from 'react'
import { Button, Empty } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { DataTable } from '@kolab/dms-base/components/ui'
import { toast } from '@kolab/dms-base/lib'
import { PaginationBar } from '../common/PaginationBar'
import { QueryStateView } from '../common/QueryStateView'
import type { useReportPagination } from '../../hooks/useReportPagination'
import { REPORT_PAGE_SIZES, type ReportFilterPatch, type ReportFilterValues } from '../../hooks/useReportFilters'
import type { ReportSortBy } from '../../api/types'

export const NO_DATA_TEXT = 'No Data Found'

type Pagination<T> = ReturnType<typeof useReportPagination<T>>

interface Props<T extends object> {
  pagination: Pagination<T>
  /** Report columns. A column whose key is a ReportSortBy is server-sortable. */
  columns: ColumnsType<T>
  rowKey: keyof T & string
  values: ReportFilterValues
  onSortChange: (patch: ReportFilterPatch) => void
  onPageSizeChange: (size: number) => void
  /** Set while the filters are invalid; the query is not run. */
  blocked?: boolean
}

const SORTABLE: ReportSortBy[] = ['date', 'deviceId']

/**
 * Page body shared by the device activity reports: pagination bar, server-
 * sorted table with a page-offset S.N column, and loading / error / empty /
 * blocked states. Sorting and paging are both server-side.
 */
export function ReportResults<T extends object>({
  pagination,
  columns,
  rowKey,
  values,
  onSortChange,
  onPageSizeChange,
  blocked,
}: Props<T>) {
  if (blocked) {
    return <Empty description="Correct the date range to see results." />
  }

  const sortedColumns: ColumnsType<T> = [
    {
      title: 'S.N',
      key: '__sn',
      width: 60,
      align: 'center',
      render: (_: unknown, __: T, index: number) => pagination.offset + index + 1,
    },
    ...columns.map((c) =>
      SORTABLE.includes(c.key as ReportSortBy)
        ? {
            ...c,
            sorter: true,
            // Never offer "unsorted": the server always orders the rows.
            sortDirections: ['descend', 'ascend', 'descend'] as ('descend' | 'ascend')[],
            sortOrder:
              values.sortBy === c.key ? (values.sortOrder === 'asc' ? ('ascend' as const) : ('descend' as const)) : null,
          }
        : c,
    ),
  ]

  return (
    <QueryStateView query={pagination.query}>
      {() => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <PaginationBar
            totalCount={pagination.totalCount}
            currentPageSize={pagination.items.length}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            onNext={pagination.goToNext}
            onPrevious={pagination.goToPrevious}
            onReset={pagination.goToFirst}
            pageSize={values.pageSize}
            pageSizeOptions={REPORT_PAGE_SIZES}
            onPageSizeChange={onPageSizeChange}
            disabled={pagination.query.isFetching}
          />
          <DataTable<T>
            rowKey={rowKey}
            showSN={false}
            columns={sortedColumns}
            dataSource={pagination.items}
            pagination={false}
            loading={pagination.query.isFetching}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={NO_DATA_TEXT} /> }}
            onChange={(_p, _f, sorter) => {
              const s = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<T>
              const key = s?.columnKey as ReportSortBy | undefined
              if (!key || !SORTABLE.includes(key) || !s.order) {
                onSortChange({ sortBy: 'date', sortOrder: 'desc' })
              } else {
                onSortChange({ sortBy: key, sortOrder: s.order === 'ascend' ? 'asc' : 'desc' })
              }
            }}
          />
        </div>
      )}
    </QueryStateView>
  )
}

/**
 * Export button for a report: downloads the server-generated CSV for the
 * current filters. Wrap it in <Can> with the report's export permission — the
 * BFF and core enforce the same permission, this only hides the control.
 */
export function ExportButton({ onExport, disabled }: { onExport: () => Promise<void>; disabled?: boolean }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button
      icon={<DownloadOutlined />}
      loading={busy}
      disabled={disabled}
      onClick={async () => {
        setBusy(true)
        try {
          await onExport()
        } catch (err) {
          toast.error((err as { message?: string })?.message || 'Export failed.')
        } finally {
          setBusy(false)
        }
      }}
    >
      Export CSV
    </Button>
  )
}
